import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
} from 'react-router-dom';

import { AuthProvider, useAuth } from './contexts/auth-context';
import { SpaceProvider, teamPath } from './contexts/space-context';

import { LoginPage } from '@/features/auth/pages/Login';
import { CliLoginPage } from '@/features/auth/pages/CliLoginPage';
import { SignUpPage } from '@/features/auth/pages/SignUp';
import { LoginFailedPage } from '@/features/auth/pages/LoginFailed';
import { DefaultErrorBoundary } from '@/shared/components/ErrorFallback';
import { Dashboard } from '@/shared/components/Dashboard';
import { AppShell } from '@/shared/shell/AppShell';
import { AgentSessionsPage } from '@/features/agent-sessions/pages/AgentSessionsPage';
import { AgentSessionPage } from '@/features/agent-sessions/pages/AgentSessionPage';
import { NewSessionPage } from '@/features/agent-sessions/pages/NewSessionPage';
import { PlanSessionPage } from '@/features/agent-sessions/pages/PlanSessionPage';
import { GetAgentTokenPage } from '@/features/agents/pages/GetAgentTokenPage';
import { SettingsShell } from '@/features/settings/pages/SettingsShell';
import { AdminGuard } from '@/features/admin/components/AdminGuard';
import { AdminLayout } from '@/features/admin/components/AdminLayout';
import { AdminOverviewPage } from '@/features/admin/pages/AdminOverviewPage';
import { AdminUsersPage } from '@/features/admin/pages/AdminUsersPage';
import { AdminTeamsPage } from '@/features/admin/pages/AdminTeamsPage';
import { AdminTeamDetailPage } from '@/features/admin/pages/AdminTeamDetailPage';
import { AdminSettingsPage } from '@/features/admin/pages/AdminSettingsPage';
import { AdminSkillDetailPage } from '@/features/admin/pages/AdminSkillDetailPage';
import { AdminSkillsPage } from '@/features/admin/pages/AdminSkillsPage';
import { AdminInvitationsPage } from '@/features/admin/pages/AdminInvitationsPage';
import { AdminBetaRequestsPage } from '@/features/admin/pages/AdminBetaRequestsPage';

const AppWrapper = () => {
  return (
    <AuthProvider>
      <SpaceProvider>
        <Dashboard>
          <Outlet />
        </Dashboard>
      </SpaceProvider>
    </AuthProvider>
  );
};

const RoutesWrapper = () => {
  return (
    <DefaultErrorBoundary>
      <Outlet />
    </DefaultErrorBoundary>
  );
};

/** Settings is team-scoped; send unscoped URLs to the personal space, preserving the
 *  sub-path and query string so bookmarks and OAuth callbacks keep working. */
const SettingsRedirect = () => {
  const { me } = useAuth();
  const { pathname, search } = useLocation();
  const rest = pathname.replace(/^\/app\/settings/, '').replace(/\/$/, '');
  // Bare /integrations now falls back to source control.
  const target = rest === '/integrations' ? '/integrations/source-control' : rest;
  return <Navigate to={`${teamPath(me.personalTeamId, '/settings')}${target}${search}`} replace />;
};

/** Land on the user's personal team's sessions list when the URL is unscoped. */
const DefaultTeamRedirect = () => {
  const { me } = useAuth();
  return <Navigate to={teamPath(me.personalTeamId, '/sessions')} replace />;
};

const router = createBrowserRouter(
  createRoutesFromElements([
    // eslint-disable-next-line react/jsx-key
    <Route path="*" element={<RoutesWrapper />}>
      <Route path="app">
        <Route element={<AppWrapper />}>
          {/* Land on the personal team's sessions list by default. */}
          <Route index element={<DefaultTeamRedirect />} />

          {/* Team-scoped agent sessions live inside the persistent app shell. */}
          <Route path="t/:teamId" element={<AppShell />}>
            <Route index element={<Navigate to="sessions" replace />} />
            {/* Repository management now lives in Settings › Source control. */}
            <Route path="repositories" element={<Navigate to="../settings/integrations/source-control" replace />} />
            <Route path="projects" element={<Navigate to="../repositories" replace />} />
            <Route path="sessions" element={<AgentSessionsPage />} />
            <Route path="sessions/new" element={<NewSessionPage />} />
            <Route path="sessions/:sessionId" element={<AgentSessionPage />} />
            {/* Plan mode: same session id, but a view with no sandbox behind it. */}
            <Route path="sessions/:sessionId/plan" element={<PlanSessionPage />} />
          </Route>

          {/* Settings — team-scoped, own full-page layout outside the shell. */}
          <Route path="t/:teamId/settings/*" element={<SettingsShell />} />
          <Route path="settings" element={<SettingsRedirect />} />
          <Route path="settings/*" element={<SettingsRedirect />} />
        </Route>
      </Route>

      <Route
        path="admin"
        element={
          <AuthProvider>
            <AdminGuard />
          </AuthProvider>
        }
      >
        <Route element={<AdminLayout />}>
          <Route index element={<AdminOverviewPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="teams" element={<AdminTeamsPage />} />
          <Route path="teams/:teamId" element={<AdminTeamDetailPage />} />
          <Route path="invitations" element={<AdminInvitationsPage />} />
          <Route path="beta-requests" element={<AdminBetaRequestsPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="skills" element={<AdminSkillsPage />} />
          <Route path="skills/:skillId" element={<AdminSkillDetailPage />} />
        </Route>
      </Route>

      <Route path="login" element={<LoginPage />} />
      <Route path="signup" element={<SignUpPage />} />
      <Route path="auth-failed" element={<LoginFailedPage />} />
      <Route
        path="get-token"
        element={
          <AuthProvider>
            <GetAgentTokenPage />
          </AuthProvider>
        }
      />
      {/*
        Deliberately NOT wrapped in AuthProvider: that redirects an unauthenticated visitor to
        /login with no return path, which would drop the `?code=` this page exists to read. The
        page checks the session itself and offers a sign-in link that carries the code through.
      */}
      <Route path="cli-login" element={<CliLoginPage />} />
      <Route index element={<Navigate to="/app" />} />
      <Route
        path="*"
        element={
          <main className="p-2">
            <p>Page not found</p>
          </main>
        }
      />
    </Route>,
  ]),
);

export const AppRoutes = () => {
  return <RouterProvider router={router} />;
};
